import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { apolloClientSSR } from 'graphql/apollo-client';
import {
  CATEGORIES,
  PRODUCT,
  RELATED_PRODUCTS,
  GET_ROUTE
} from 'graphql/queries';
// import getProductImage from 'utils/getProductImage';
import viewProductEvent from 'lib/Google/events/viewProduct.gtag';
import useProductBreadcrumbs from 'hooks/products/useProductBreadcrumbs';
import SEO from '@/SEO';
import Layout from '@/UI/Layout';
import { Container } from '@/UI/Layout/Container';
import Breadcrumbs from '@/UI/Breadcrumbs';
import MainDetails from '@/Product/Main';
import Tabs from '@/Product/Tabs';
import Reviews from '@/Product/Reviews';
// import Recommended from '@/Product/Recommended';
// import Related from '@/Product/Related';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { CatalogueContainer, ResultsContainer } from '@/Catalogue/styles';
import Products from '@/Catalogue/Products';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useTranslation } from 'next-i18next';
import { SectionTitle } from '@/UI/Primitives/Headings';

export default function ProductPage({
  product,
  categories,
  error,
  locale,
  urlKey
}) {
  const { t } = useTranslation();

  const [relatedProducts, setRelatedProducts] = useState([]);

  const [getRelatedProducts, { data, loading }] = useLazyQuery(
    RELATED_PRODUCTS,
    {
      variables: { urlKey: urlKey },
      context: { headers: { store: locale }, fetchOptions: { method: 'GET' } }
    }
  );

  // FETCH RELATED PRODUCTS - Fixed to update when urlKey changes
  useEffect(() => {
    const fetchRelatedProducts = async () => {
      try {
        // Clear previous related products when navigating to new product
        setRelatedProducts([]);
        
        const related = await getRelatedProducts({
          variables: { urlKey: urlKey },
          context: { headers: { store: locale }, fetchOptions: { method: 'GET' } }
        });
        
        setRelatedProducts(related?.data?.products?.items[0]?.related_products || []);
      } catch (error) {
        console.error('Error fetching related products:', error);
        setRelatedProducts([]);
      }
    };

    if (urlKey) {
      fetchRelatedProducts();
    }
  }, [urlKey, locale, getRelatedProducts]); // Added dependencies

// Facebook Pixel tracking
useEffect(() => {
  if (product) {
    const categoryName = product?.categories && product.categories.length > 0 
      ? product.categories[product.categories.length - 1]?.name 
      : 'Unknown';

    fbq('track', 'ViewContent', {
      content_type: 'product',
      content_ids: [product?.id],
      content_name: product?.name,
      content_category: categoryName,
      value: product?.price_range?.maximum_price?.final_price?.value,
      currency: 'EUR'
    });
  }
}, [product]);

  const router = useRouter();
  const breadcrumbs = useProductBreadcrumbs(product);

  const description = product?.short_description?.html?.replace(
    /<(.|\n)*?>/g,
    ''
  );
  /* const image = getProductImage({
    mainImage: product?.image,
    gallery: product?.media_gallery
  }); */

  // Google Analytics tracking
  useEffect(() => {
    if (product) viewProductEvent(product); // Send product view event to Google Analytics
  }, [product]);

  // Redirect on error
  useEffect(() => {
    if (error) {
      router.push('/');
    }
  }, [error, router]);

  if (error) return null;

  return (
    <Layout categories={categories}>
      <SEO
        seo={{
          title: `${product?.name} - Baltbaby`,
          description,
          ...(product?.image?.url ? { image: product?.image?.url } : null)
        }}
      />
      <Script
        type="application/ld+json"
        id="baltbaby_product_structured_data"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'http://schema.org',
            '@type': 'Product',
            name: product?.name,
            sku: product?.sku,
            image: product.image ? product.image.url : null,
            ...(description ? { description } : null),
            ...(product?.mpbrand?.value
              ? {
                  brand: {
                    '@type': 'Brand',
                    name: product?.mpbrand?.value
                  }
                }
              : null),
            offers: {
              '@type': 'Offer',
              url: `https://baltbaby.com${router.asPath}`,
              priceCurrency: 'EUR',
              price: product?.price_range?.minimum_price?.final_price.value
            }
          })
        }}
      />

      <Container>
        <Breadcrumbs crumbs={breadcrumbs} />
        <MainDetails product={product} />
        <Tabs
          description={product?.description?.html}
          brand={product?.mpbrand}
        />
        {/* {product?.crosssell_products?.length > 0 && (
          <Recommended products={product?.crosssell_products} />
        )} */}
        <Reviews product={product} />
        {/* {product?.related_products.length > 0 && (
          <Related products={product?.related_products} />
        )} */}
        {relatedProducts?.length > 0 && (
          <>
            <SectionTitle
              as="h2"
              fontSize="large"
              pt={{ desktop: 'rem' }}
              pb={{
                _: '1rem',
                desktop: '0.5rem'
              }}
            >
              {t('product_main.related')}
            </SectionTitle>
            <CatalogueContainer>
              <ResultsContainer>
                <Products products={relatedProducts} />
              </ResultsContainer>
            </CatalogueContainer>
          </>
        )}
      </Container>
    </Layout>
  );
}

export async function getServerSideProps(context) {
  let urlKey = context.params?.toode;

  const cookie = context.req.headers?.cookie;

  const redirectData = await apolloClientSSR(context.locale, cookie).query({
    query: GET_ROUTE,
    variables: { url: `${urlKey}.html` },
    fetchPolicy: 'network-only'
  });

  // CHECK URL REWRITES
  if (redirectData?.data?.route?.relative_url) {
    //CHECK IF OLD URL === NEW URL
    const relativeURL = redirectData?.data?.route?.relative_url.replace(
      '.html',
      ''
    );
    if (relativeURL !== urlKey) {
      //CHECK IF WE STILL GET THE PRODUCT FROM OLD URL
      const productTest = await apolloClientSSR(context.locale, cookie).query({
        query: PRODUCT,
        variables: { urlKey: urlKey },
        fetchPolicy: 'network-only'
      });
      //PRODUCT NOT FOUND
      if (productTest?.data?.products?.items.length === 0) {
        urlKey = relativeURL;

        const productTestNew = await apolloClientSSR(
          context.locale,
          cookie
        ).query({
          query: PRODUCT,
          variables: { urlKey: relativeURL },
          fetchPolicy: 'network-only'
        });
        if (productTestNew?.data?.products?.items.length > 0) {
          return {
            redirect: {
              permanent: true,
              destination: `/toode/${relativeURL}`
            }
          };
        } else {
          return {
            redirect: {
              permanent: false,
              destination: `/`
            }
          };
        }
      }
    }
  }
  const promiseProduct = apolloClientSSR(context.locale, cookie).query({
    query: PRODUCT,
    variables: { urlKey: urlKey },
    fetchPolicy: 'network-only'
  });

  const promiseCategories = apolloClientSSR(context.locale, cookie).query({
    query: CATEGORIES,
    variables: { ids: '2' },
    context: { fetchOptions: { method: 'GET' } }
  });

  const [productData, categoriesData] = await Promise.all([
    promiseProduct,
    promiseCategories
  ]);

  const product = productData.data?.products?.items[0] ?? null;
  return {
    props: {
      product,
      error: product === null,
      categories: categoriesData.data.categoryList
        ? categoriesData.data.categoryList[0].children
        : [],
      ...(await serverSideTranslations(context.locale)),
      locale: context.locale ? context.locale : 'et',
      urlKey: urlKey
    }
  };
}